import React from "react";
import {Box, Button, Typography, makeStyles, useTheme} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import {grey} from "@material-ui/core/colors";
import UploadIcon from "view/images/UploadIcon";
import {TaskAttachments} from "view/components/TaskDrawer";
import {unpackFileAttachment} from "tools/product/mapAttachmentFieldToTaskAttachment";
import {attachmentFileFormats} from "constraints/general";
import {RawAttachmentFile} from "ts/api/attachmentFile";
import {
  FILE_SIZE_LIMIT_ERROR,
  MAX_FILE_SIZE_HELP_TEXT,
  hasOversizedFile,
} from "tools/file/fileSizeLimit";
import {cutOffFileTitle} from "../shared";

const useStyles = makeStyles((theme) => ({
  uploadRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
  },
  caption: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: grey[600],
  },
  fileInput: {
    display: "none",
  },
  attachments: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
  errorAlert: {
    marginTop: theme.spacing(2),
  },
}));

type Props = {
  /** Unique per hosting modal — pairs the hidden input with its label. */
  inputId: string;
  files: RawAttachmentFile[];
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (id: string) => void;
};

/**
 * File section of the Approve / Reject / Revision pop-ups: the
 * "FILE (MAX. FILE SIZE 10 MB) … ⬆ UPLOAD" row right under the message
 * editor, the attached-file cards below it, and the inline over-limit
 * error. An oversized file stays in the list so the user can see and
 * remove it — the hosting modal disables Submit off `hasOversizedFile`.
 */
const ReactionAttachments: React.FC<Props> = ({
  inputId,
  files,
  onUpload,
  onRemove,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <Box>
      <Box className={classes.uploadRow}>
        <Typography className={classes.caption}>
          {MAX_FILE_SIZE_HELP_TEXT}
        </Typography>
        <Box>
          <input
            accept={attachmentFileFormats}
            className={classes.fileInput}
            id={inputId}
            name={inputId}
            type="file"
            multiple
            onChange={onUpload}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor={inputId}>
            <Button
              color="primary"
              component="span"
              startIcon={<UploadIcon color={theme.palette.primary.main} />}
            >
              Upload
            </Button>
          </label>
        </Box>
      </Box>
      {files.length > 0 && (
        <TaskAttachments
          hideTitle
          className={classes.attachments}
          onRemoveAttachment={onRemove}
          attachments={files.map((file) =>
            unpackFileAttachment(cutOffFileTitle(file))
          )}
        />
      )}
      {hasOversizedFile(files) && (
        <Alert severity="error" className={classes.errorAlert}>
          {FILE_SIZE_LIMIT_ERROR}
        </Alert>
      )}
    </Box>
  );
};

export default ReactionAttachments;
